import { useState, useMemo } from 'react'
import TableSortLabel from '@mui/material/TableSortLabel'

const contentBlock = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12
}

const scrollWrap = {
  width: '100%',
  overflowX: 'hidden',
  background: '#fff',
  border: '1px solid #d4d4d4',
  borderRadius: 10
}

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'auto'
}

const thStyle = {
  textAlign: 'left',
  background: '#c8e1e1',
  borderBottom: '1px solid #b4d6cb',
  padding: '10px 6px',
  fontSize: 14,
  whiteSpace: 'normal',
  lineHeight: 1.2,
  verticalAlign: 'middle'
}

const tdStyle = {
  padding: '9px 6px',
  borderBottom: '1px solid #ececec',
  fontSize: 14,
  whiteSpace: 'nowrap'
}

const normalize = (value) => String(value ?? '').trim().toLowerCase()

const StatsEvVehiclesModal = ({ stats, t, fmtNum, filterCriteria = {} }) => {
  const [orderBy, setOrderBy] = useState('name')
  const [order, setOrder] = useState('asc')

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(col)
      setOrder('asc')
    }
  }

  const filteredVehicles = useMemo(() => {
    const items = Array.isArray(stats?.vehiclesStats)
      ? stats.vehiclesStats
      : []

    const selectedVehicleId = normalize(
      filterCriteria?.vehicleId ??
      filterCriteria?.vehicle ??
      ''
    )

    const selectedVehicleName = normalize(
      filterCriteria?.vehicleName ??
      filterCriteria?.name ??
      ''
    )

    return items.filter((vehicle) => {
      const vehicleId = normalize(vehicle?.id)
      const vehicleName = normalize(vehicle?.name)
      const vehicleLicense = normalize(vehicle?.license)

      let match = true

      if (selectedVehicleId) {
        match = vehicleId === selectedVehicleId
      }

      if (selectedVehicleName) {
        match =
          vehicleName === selectedVehicleName ||
          vehicleLicense === selectedVehicleName
      }

      if (!match) return false

      const hasData =
        Number(vehicle.trips) > 0 ||
        Number(vehicle.km) > 0 ||
        Number(vehicle.electricCost) > 0 ||
        Number(vehicle.electricKwh) > 0

      return hasData
    })
  }, [stats?.vehiclesStats, filterCriteria])

  const sortedVehicles = useMemo(() => {
    return [...filteredVehicles].sort((a, b) => {
      let aVal = a?.[orderBy]
      let bVal = b?.[orderBy]

      if (
        orderBy === 'trips' ||
        orderBy === 'km' ||
        orderBy === 'electricKwh' ||
        orderBy === 'electricKwhPer100Km' ||
        orderBy === 'electricCost' ||
        orderBy === 'electricCostPer100Km' ||
        orderBy === 'avgElectricPrice'
      ) {
        aVal = Number(aVal) || 0
        bVal = Number(bVal) || 0
      } else {
        aVal = String(aVal ?? '').toLowerCase()
        bVal = String(bVal ?? '').toLowerCase()
      }

      if (aVal > bVal) return order === 'asc' ? 1 : -1
      if (aVal < bVal) return order === 'asc' ? -1 : 1
      return 0
    })
  }, [filteredVehicles, orderBy, order])

  return (
    <div style={contentBlock}>
      <div style={scrollWrap}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  {t('TRIPLOG.STATISTICS.VEHICLES.VEHICLE_NAME')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'license'}
                  direction={orderBy === 'license' ? order : 'asc'}
                  onClick={() => handleSort('license')}
                >
                  {t('TRIPLOG.STATISTICS.VEHICLES.LICENSE_PLATE')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'trips'}
                  direction={orderBy === 'trips' ? order : 'asc'}
                  onClick={() => handleSort('trips')}
                >
                  {t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_TRIPS')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'km'}
                  direction={orderBy === 'km' ? order : 'asc'}
                  onClick={() => handleSort('km')}
                >
                  {t('TRIPLOG.STATISTICS.VEHICLES.KM')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'electricKwh'}
                  direction={orderBy === 'electricKwh' ? order : 'asc'}
                  onClick={() => handleSort('electricKwh')}
                >
                  {t('TRIPLOG.STATISTICS.EV.TOTAL_KWH')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'electricKwhPer100Km'}
                  direction={orderBy === 'electricKwhPer100Km' ? order : 'asc'}
                  onClick={() => handleSort('electricKwhPer100Km')}
                >
                  {t('TRIPLOG.STATISTICS.EV.KWH_PER_100KM')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'electricCost'}
                  direction={orderBy === 'electricCost' ? order : 'asc'}
                  onClick={() => handleSort('electricCost')}
                >
                  {t('TRIPLOG.STATISTICS.EV.TOTAL_COST')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'electricCostPer100Km'}
                  direction={orderBy === 'electricCostPer100Km' ? order : 'asc'}
                  onClick={() => handleSort('electricCostPer100Km')}
                >
                  {t('TRIPLOG.STATISTICS.EV.COST_PER_100KM')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'avgElectricPrice'}
                  direction={orderBy === 'avgElectricPrice' ? order : 'asc'}
                  onClick={() => handleSort('avgElectricPrice')}
                >
                  {t('TRIPLOG.STATISTICS.EV.AVG_PRICE')}
                </TableSortLabel>
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedVehicles.map((vehicle) => (
              <tr key={vehicle.id}>
                <td style={tdStyle}>{vehicle.name}</td>
                <td style={tdStyle}>{vehicle.license}</td>
                <td style={tdStyle}>{fmtNum(vehicle.trips)}</td>
                <td style={tdStyle}>{fmtNum(vehicle.km, 2)} {t('TRIPLOG.UNITS.KM')}</td>
                <td style={tdStyle}>{fmtNum(vehicle.electricKwh, 2)} kWh</td>
                <td style={tdStyle}>{fmtNum(vehicle.electricKwhPer100Km, 2)} kWh/100 km</td>
                <td style={tdStyle}>{fmtNum(vehicle.electricCost, 2)} {t('TRIPLOG.UNITS.EUR')}</td>
                <td style={tdStyle}>{fmtNum(vehicle.electricCostPer100Km, 2)} €/100 km</td>
                <td style={tdStyle}>{fmtNum(vehicle.avgElectricPrice, 3)} €/kWh</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default StatsEvVehiclesModal